/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatStoredDateTime } from '@/utils/datetime';

const ConnectionItem = ({ connection }) => {
  const getHealthColor = (status) => {
    if (status === 'UNHEALTHY') return 'text-red-500';
    return 'text-green-500';
  };

  // Defensively check for connection and its nested properties
  if (!connection) {
    return null;
  }

  const {
    channel_name,
    user_id,
    account_id,
    connection_type,
    health_status,
    connection_details,
    last_client_message,
    last_server_message,
    connected_at,
    last_client_message_at,
    last_server_message_at,
  } = connection;

  const renderJson = (data) => {
    if (!data || Object.keys(data).length === 0) return 'N/A';
    return JSON.stringify(data, null, 2);
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle>Connection: {channel_name || 'N/A'}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p><strong>User ID:</strong> {user_id || 'N/A'}</p>
            <p><strong>Account ID:</strong> {account_id || 'N/A'}</p>
            <p><strong>Connection Type:</strong> {connection_type || 'N/A'}</p>
            <div className="mt-4">
              <strong>Health Status:</strong>
              {health_status ? (
                <>
                  <p className={getHealthColor(health_status.client_to_backend)}>
                    Client to Backend: {health_status.client_to_backend || 'N/A'}
                  </p>
                  <p className={getHealthColor(health_status.backend_to_mt5)}>
                    Backend to MT5: {health_status.backend_to_mt5 || 'N/A'}
                  </p>
                </>
              ) : (
                <p>N/A</p>
              )}
            </div>
          </div>
          <div>
            <p>
              <strong>Connected At:</strong>{' '}
              {formatStoredDateTime(connected_at, undefined, 'N/A')}
            </p>
            <p>
              <strong>Last Client Message At:</strong>{' '}
              {formatStoredDateTime(last_client_message_at, undefined, 'N/A')}
            </p>
             <p>
              <strong>Last Server Message At:</strong>{' '}
              {formatStoredDateTime(last_server_message_at, undefined, 'N/A')}
            </p>
          </div>
          <div className="col-span-1 md:col-span-2">
            <strong>Details:</strong>
            <pre className="bg-gray-100 p-2 rounded-md dark:bg-gray-800 text-sm">
              {renderJson(connection_details)}
            </pre>
          </div>
          <div className="col-span-1 md:col-span-2">
            <strong>Last Client Message:</strong>
            <pre className="bg-gray-100 p-2 rounded-md dark:bg-gray-800 text-sm">
              {renderJson(last_client_message)}
            </pre>
          </div>
          <div className="col-span-1 md:col-span-2">
            <strong>Last Server Message:</strong>
            <pre className="bg-gray-100 p-2 rounded-md dark:bg-gray-800 text-sm">
              {renderJson(last_server_message)}
            </pre>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ConnectionItem;
